import { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { Daycare } from '@/types/Daycare'
import { Section } from '@/types/Section'
import { useLazyQuery, useQuery } from '@apollo/client'
import { GET_SECTIONS } from '@/modules/API/daycare/sectionHandler'
import styles from './childsTableHandler.module.scss'
import { useTranslation } from 'react-i18next'
import Loading from '@/components/Loading/Loading'
import Title from '@/components/_custom/Title/title'
import { RegularSlot } from '@/types/Slots'
import { GET_REGULAR_SLOTS } from '@/modules/API/slot/regularSlotHandler'
import moment from 'moment/moment'
import Desktop from '@/modules/desktopMobile/desktop'
import { ChildWithParents } from '@/types/Child'
import Indicator from '@/components/_custom/Indicator/indicator'
import { getDaycareDaysOpening } from '@/modules/helper/daycareHelper'
import ChildsTable from './childsTable/childsTable'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import Mobile from '@/modules/desktopMobile/mobile'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { Add, Restaurant, Settings, SwapHoriz, VisibilityOff } from '@mui/icons-material'
import AddFamilyDialog from '../header/Dialog/addFamilyDialog.tsx'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import BulkHideChildrenDialog from './childsTable/Dialog/bulkHideChildrenDialog'
import BulkChangeSectionDialog from './childsTable/Dialog/bulkChangeSectionDialog'
import BulkChangeMealDialog from './childsTable/Dialog/bulkChangeMealDialog'
import { useDialog } from '@/hooks/useDialog/useDialog.tsx'

type BulkDialog = 'hide' | 'section' | 'meal' | null

interface ChildsTableHandlerProps {
    currentDaycare: Daycare | undefined
    childs: ChildWithParents[]
    canWriteRegular: boolean
    canReadRegular: boolean
    refetchAllParent: () => void
    refetchAllChilds: () => void
    isRpeOrLaep: boolean
}

const ChildsTableHandler: FunctionComponent<ChildsTableHandlerProps> = ({
    currentDaycare,
    childs,
    canWriteRegular,
    canReadRegular,
    refetchAllParent,
    refetchAllChilds,
    isRpeOrLaep
}: ChildsTableHandlerProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const [childList, setChildList] = useState<ChildWithParents[]>(childs)

    const [sectionsList, setSectionsList] = useState<Section[]>([])

    const [allChilds, setAllChilds] = useState<number>(0)
    const [childsWithoutContract, setChildsWithoutContract] = useState<number>(0)
    const [childsWithContract, setChildsWithContract] = useState<number>(0)
    const [childsAllDayInContract, setChildsAllDayInContract] = useState<number>(0)

    const [isAddFamilyDialogOpen, setAddFamilyDialogOpen] = useState<boolean>(false)

    const [autoLinkDaycare, setAutoLinkDaycare] = useState<string>('')
    const [autoLinkActive, setAutoLinkActive] = useState<boolean>(false)

    const [selectedChildIds, setSelectedChildIds] = useState<number[]>([])
    const [openBulkDialog, setOpenBulkDialog] = useState<BulkDialog>(null)

    const { dialog: bulkChangeSectionDialog, openDialog: openBulkChangeSectionDialog } = useDialog({
        dialogTitle: 'bulkChangeSection',
        Component: BulkChangeSectionDialog,
        props: (handleCloseDialog) => ({
            handleCloseDialog,
            selectedChildIds,
            sectionsList,
            onSuccess: handleBulkSuccess
        }),
        hideDialogClose: true
    })

    function toggleChild(childId: number): void {
        setSelectedChildIds((prev: number[]): number[] =>
            prev.includes(childId) ? prev.filter((id: number): boolean => id !== childId) : [...prev, childId]
        )
    }

    function toggleSection(childIds: number[], select: boolean): void {
        setSelectedChildIds((prev: number[]): number[] =>
            select
                ? [...prev, ...childIds.filter((id: number): boolean => !prev.includes(id))]
                : prev.filter((id: number): boolean => !childIds.includes(id))
        )
    }

    function toggleAll(select: boolean): void {
        setSelectedChildIds(select ? childList.map((child: ChildWithParents): number => child.id) : [])
    }

    function handleBulkSuccess(): void {
        setSelectedChildIds([])
        refetchAllChilds()
    }

    useEffect(() => {
        setAutoLinkDaycare(currentDaycare?.autoLink || '')
        setAutoLinkActive(!!currentDaycare?.autoLink)
    }, [currentDaycare])

    const daycareDaysOpening: number[] = currentDaycare ? getDaycareDaysOpening(currentDaycare) : []

    const { loading: loadingSections, data: dataSections } = useQuery(GET_SECTIONS, {
        variables: {
            daycare_id: currentDaycare?.id
        }
    })

    useEffect(() => {
        if (dataSections) {
            setSectionsList(dataSections.getSections)
        }
    }, [dataSections])

    const { fromDate, toDate } = useMemo(() => {
        return {
            fromDate: moment(),
            toDate: moment().add(1, 'years')
        }
    }, [])

    const [regularSlots, setRegularSlots] = useState<RegularSlot[]>([])

    const [getAllRegularSlots, { loading: loadingRegularSlots, data: dataRegularSlots }] = useLazyQuery(
        GET_REGULAR_SLOTS,
        {
            variables: {
                daycareId: currentDaycare?.id,
                from: fromDate.format('DD-MM-YYYY'),
                to: toDate.format('DD-MM-YYYY')
            }
        }
    )

    useEffect(() => {
        if (dataRegularSlots) {
            setRegularSlots(
                dataRegularSlots.getRegularSlots
                    .slice()
                    .sort((eventA: RegularSlot, eventB: RegularSlot): number => {
                        return moment(eventA.start, 'HH:mm:ss').format('HH:mm') >
                            moment(eventB.start, 'HH:mm:ss').format('HH:mm')
                            ? -1
                            : 1
                    })
                    .sort((eventA: RegularSlot, eventB: RegularSlot): number => {
                        return eventA.day > eventB.day ? 1 : -1
                    })
            )
        }
    }, [dataRegularSlots])

    useEffect(() => {
        if (childList) {
            setAllChilds(childList.length)
            setChildsWithoutContract(getChildsWithoutContract(childList, regularSlots))
            setChildsWithContract(getChildsWithContract(childList, regularSlots))
            setChildsAllDayInContract(getChildsAllDayInContract(childList, regularSlots))
        }
    }, [childList, regularSlots])

    function resetFilter() {
        setChildList(childs)
    }

    function filterChildsWithoutContract() {
        setChildList(
            childs.filter((child: ChildWithParents) => {
                return !regularSlots.find((regularSlot: RegularSlot): boolean => regularSlot.childId === child.id)
            })
        )
    }

    function filterChildsWithContract() {
        setChildList(
            childs.filter((child: ChildWithParents) => {
                const nbOfDayInContract: number[] = regularSlots
                    .filter((regularSlot: RegularSlot): boolean => regularSlot.childId === child.id)
                    .map((regularSlot: RegularSlot): number => regularSlot.day)

                const setNbOfDayInContract: Set<number> = new Set(nbOfDayInContract)
                return setNbOfDayInContract.size > 0 && setNbOfDayInContract.size < daycareDaysOpening.length
            })
        )
    }

    function filterChildsAllDayInContract() {
        setChildList(
            childs.filter((child: ChildWithParents): boolean => {
                const nbOfDayInContract: number[] = regularSlots
                    .filter((regularSlot: RegularSlot): boolean => regularSlot.childId === child.id)
                    .map((regularSlot: RegularSlot): number => regularSlot.day)

                const setNbOfDayInContract: Set<number> = new Set(nbOfDayInContract)
                return setNbOfDayInContract.size === daycareDaysOpening.length
            })
        )
    }

    function getChildsWithoutContract(childs: ChildWithParents[], regularSlots: RegularSlot[]): number {
        let count: number = 0
        childs.map((child: ChildWithParents): void => {
            if (!regularSlots.find((regularSlot: RegularSlot): boolean => regularSlot.childId === child.id)) {
                count += 1
            }
        })
        return count
    }

    function getChildsWithContract(childs: ChildWithParents[], regularSlots: RegularSlot[]): number {
        let count: number = 0
        childs.map((child: ChildWithParents): void => {
            const nbOfDayInContract: number[] = regularSlots
                .filter((regularSlot: RegularSlot): boolean => regularSlot.childId === child.id)
                .map((regularSlot: RegularSlot): number => regularSlot.day)
            const setNbOfDayInContract: Set<number> = new Set(nbOfDayInContract)

            if (setNbOfDayInContract.size > 0 && setNbOfDayInContract.size < daycareDaysOpening.length) {
                count += 1
            }
        })
        return count
    }

    function getChildsAllDayInContract(childs: ChildWithParents[], regularSlots: RegularSlot[]): number {
        let count: number = 0
        childs.map((child: ChildWithParents): void => {
            const nbOfDayInContract: number[] = regularSlots
                .filter((regularSlot: RegularSlot): boolean => regularSlot.childId === child.id)
                .map((regularSlot: RegularSlot): number => regularSlot.day)
            const setNbOfDayInContract: Set<number> = new Set(nbOfDayInContract)

            if (setNbOfDayInContract.size === daycareDaysOpening.length) {
                count += 1
            }
        })
        return count
    }

    useEffect(() => {
        resetFilter()
        setSelectedChildIds([])
        if (canReadRegular) getAllRegularSlots()
    }, [childs])

    const canWriteFamilies: boolean = !!user?.currentDaycareRights.canWriteFamilies
    const canWriteMeal: boolean = !!user?.currentDaycareRights.canWriteMeal && !!currentDaycare?.mealAccess

    return (
        <>
            <div className={styles.IndicatorsContainer}>
                <a onClick={resetFilter} className={styles.Clickable}>
                    <Indicator count={allChilds} backgroundWhite text={t('all')} />
                </a>
                {canReadRegular && (
                    <>
                        <a onClick={filterChildsWithoutContract} className={styles.Clickable}>
                            <Indicator
                                count={childsWithoutContract}
                                backgroundWhite
                                text={childsWithoutContract > 1 ? t('withoutContracts') : t('withoutContract')}
                            />
                        </a>
                        <Desktop>
                            {childsWithContract > 0 && (
                                <a onClick={filterChildsWithContract} className={styles.Clickable}>
                                    <Indicator
                                        count={childsWithContract}
                                        backgroundWhite
                                        text={childsWithContract > 1 ? t('partialContracts') : t('partialContract')}
                                    />
                                </a>
                            )}
                            {childsAllDayInContract > 0 && (
                                <a onClick={filterChildsAllDayInContract} className={styles.Clickable}>
                                    <Indicator
                                        count={childsAllDayInContract}
                                        backgroundWhite
                                        text={childsAllDayInContract > 1 ? t('fullContracts') : t('fullContract')}
                                    />
                                </a>
                            )}
                        </Desktop>
                    </>
                )}
            </div>
            {user?.currentDaycareRights.canWriteFamilies && (
                <Mobile>
                    <div className={styles.Parameters}>
                        <ButtonTextIcon
                            type={'custom'}
                            onClick={() => setAddFamilyDialogOpen(true)}
                            customIcon={<Add />}
                            customText={isRpeOrLaep ? t('invitePublic') : t('inviteFamily')}
                        />
                        <Settings
                            className={styles.Icon}
                            onClick={() => navigate(`/daycare?daycare=${currentDaycare?.id}#settings`)}
                        />
                        <AddFamilyDialog
                            open={isAddFamilyDialogOpen}
                            closeDialog={() => setAddFamilyDialogOpen(false)}
                            daycare={currentDaycare}
                            isRpeOrLaep={isRpeOrLaep}
                            autoLinkActive={autoLinkActive}
                            autoLinkDaycare={autoLinkDaycare}
                            refetchAllParent={refetchAllParent}
                            refetchAllChilds={refetchAllChilds}
                        />
                    </div>
                </Mobile>
            )}
            <div className={styles.ContractPosition}>
                <div className={styles.TitleAndTabs}>
                    <div className={styles.Title}>
                        <Title text={t('myChilds')} />
                    </div>
                    {selectedChildIds.length > 0 && currentDaycare && (canWriteFamilies || canWriteMeal) && (
                        <div className={styles.BulkActions}>
                            <p className={styles.SelectedCount}>
                                {selectedChildIds.length}{' '}
                                {selectedChildIds.length > 1 ? t('childsSelected') : t('childSelected')}
                            </p>
                            {canWriteFamilies && (
                                <>
                                    <ButtonTextIcon
                                        type={'custom'}
                                        onClick={() => setOpenBulkDialog('hide')}
                                        customIcon={<VisibilityOff />}
                                        customText={t('bulkHideChilds')}
                                        className={styles.Button}
                                    />
                                    <ButtonTextIcon
                                        type={'custom'}
                                        onClick={openBulkChangeSectionDialog}
                                        customIcon={<SwapHoriz />}
                                        customText={t('bulkChangeSection')}
                                        className={styles.Button}
                                    />
                                </>
                            )}
                            {canWriteMeal && (
                                <ButtonTextIcon
                                    type={'custom'}
                                    onClick={() => setOpenBulkDialog('meal')}
                                    customIcon={<Restaurant />}
                                    customText={t('bulkChangeMeal')}
                                    className={styles.Button}
                                />
                            )}
                        </div>
                    )}
                </div>
                {(loadingSections || loadingRegularSlots) && <Loading overall={false} />}
                <div className={styles.ContractContainer}>
                    <ChildsTable
                        childList={
                            childList
                                ? [...childList].sort((a: ChildWithParents, b: ChildWithParents) => {
                                      return a.firstname.localeCompare(b.firstname)
                                  })
                                : []
                        }
                        sectionsList={sectionsList}
                        daycare={currentDaycare}
                        canWriteRegular={canWriteRegular}
                        canReadRegular={canReadRegular}
                        regularSlots={regularSlots}
                        selectedChildIds={selectedChildIds}
                        onToggleChild={toggleChild}
                        onToggleSection={toggleSection}
                        onToggleAll={toggleAll}
                    />
                </div>
            </div>
            {currentDaycare && openBulkDialog === 'hide' && (
                <BulkHideChildrenDialog
                    isDialogOpen={true}
                    handleCloseDialog={() => setOpenBulkDialog(null)}
                    selectedChildIds={selectedChildIds}
                    daycareId={currentDaycare.id}
                    onSuccess={handleBulkSuccess}
                />
            )}
            {bulkChangeSectionDialog}
            {currentDaycare && openBulkDialog === 'meal' && (
                <BulkChangeMealDialog
                    isDialogOpen={true}
                    handleCloseDialog={() => setOpenBulkDialog(null)}
                    selectedChildIds={selectedChildIds}
                    daycareId={currentDaycare.id}
                    onSuccess={handleBulkSuccess}
                />
            )}
        </>
    )
}

export default ChildsTableHandler
